import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faClock, faUpload, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import MatterService from '@frontend/AppSettings/services/MatterService';
import toastService from '@frontend/AppSettings/services/ToastService';
import AddTeamMemberModal from './TeamWidget/AddTeamMemberModal';
import CreateTaskModal from './TasksTable/Components/TaskData/CreateTask/CreateTaskModal';
import LogBillableEntryModal from '../../Financial/Billing/Components/LogBillableEntryModal/LogBillableEntryModal';
import './TeamWidget/TeamWidget.css';

const actions = [
  { id: 'addTask', icon: faPlus, label: 'Add Task' },
  { id: 'logTime', icon: faClock, label: 'Log Time' },
  { id: 'uploadDocument', icon: faUpload, label: 'Upload Document' },
  { id: 'addTeamMember', icon: faUserPlus, label: 'Add Team Member' },
] as const;

const QuickActionsWidget: React.FC<{ onOpenFilesTab?: () => void }> = ({ onOpenFilesTab }) => {
  const { matterId } = useParams<{ matterId: string }>();
  const navigate = useNavigate();

  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showLogTime, setShowLogTime] = useState(false);
  const [showAddTeamMember, setShowAddTeamMember] = useState(false);
  const [addTeamMemberEmployeeIds, setAddTeamMemberEmployeeIds] = useState<string[]>([]);
  const [addTeamMemberLoading, setAddTeamMemberLoading] = useState(false);

  const fetchCurrentEmployeeIds = useCallback(async (): Promise<string[]> => {
    if (!matterId) return [];
    try {
      const details = await MatterService.getMatterDetails(matterId);
      const ids: string[] = [];
      if (details?.employeeid) {
        ids.push(String(details.employeeid));
      }
      if (Array.isArray(details?.associated_employees)) {
        details.associated_employees.forEach((emp: { employeeid?: string | number; employeeId?: string | number }) => {
          const id = emp.employeeid ?? emp.employeeId;
          if (id != null) ids.push(String(id));
        });
      }
      return ids;
    } catch {
      return [];
    }
  }, [matterId]);

  const handleAddTask = () => {
    if (matterId) setShowCreateTask(true);
  };

  const handleLogTime = () => {
    if (matterId) setShowLogTime(true);
  };

  const handleUploadDocument = () => {
    if (!matterId) return;
    if (onOpenFilesTab) {
      onOpenFilesTab();
    } else {
      navigate(`/matterDetails/${matterId}/files`);
    }
  };

  const handleAddTeamMember = async () => {
    if (!matterId) return;
    setAddTeamMemberLoading(true);
    try {
      const ids = await fetchCurrentEmployeeIds();
      setAddTeamMemberEmployeeIds(ids);
      setShowAddTeamMember(true);
    } finally {
      setAddTeamMemberLoading(false);
    }
  };

  const handleAction = (id: typeof actions[number]['id']) => {
    switch (id) {
      case 'addTask':
        handleAddTask();
        break;
      case 'logTime':
        handleLogTime();
        break;
      case 'uploadDocument':
        handleUploadDocument();
        break;
      case 'addTeamMember':
        handleAddTeamMember();
        break;
      default:
        break;
    }
  };

  return (
    <>
      <div className="sidebar-card">
        <div className="sidebar-card__header">
          <div className="sidebar-card__title">Quick Actions</div>
        </div>
        <div className="qa-actions">
          {actions.map((action) => (
            <button
              key={action.id}
              type="button"
              className="qa-action-btn"
              onClick={() => handleAction(action.id)}
              disabled={!matterId || (action.id === 'addTeamMember' && addTeamMemberLoading)}
            >
              <FontAwesomeIcon icon={action.icon} />
              <span>
                {action.id === 'addTeamMember' && addTeamMemberLoading
                  ? 'Loading...'
                  : action.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {matterId && (
        <>
          <CreateTaskModal
            isOpen={showCreateTask}
            onClose={() => setShowCreateTask(false)}
            matterId={matterId}
          />
          <LogBillableEntryModal
            isOpen={showLogTime}
            onClose={() => setShowLogTime(false)}
            onSuccess={() => toastService.success('Billable entry logged successfully.')}
            matterId={matterId}
          />
          {showAddTeamMember && (
            <AddTeamMemberModal
              matterId={matterId}
              currentEmployeeIds={addTeamMemberEmployeeIds}
              onClose={() => setShowAddTeamMember(false)}
              onAdded={() => setShowAddTeamMember(false)}
            />
          )}
        </>
      )}
    </>
  );
};

export default QuickActionsWidget;
